import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Spin,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { measurementDateKey } from '../utils/measurementTime';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import Report from '../components/Report';
import {
    SectionConfigKpiCards,
    SectionConfigBenchmarkCards,
    SectionConfigBenchmarkRadar,
    SectionConfigTable,
    SectionConfigLine,
    SectionConfigPatternPointLine,
    SectionConfigHeatmap,
    SectionConfigAreaHierarchy,
    SectionConfigAnimatedHeatmap,
    SectionConfigAnimatedPatternPoints,
    SectionConfigPatternList,
    SectionConfigMeasurementList,
    SectionConfigMeasurementReport,
    SectionConfigWarningCard,
    SectionConfigCalendarAvailability,
    SectionConfigDailyKpiHistory,
} from '../components/ReportWizard';
import type { HeatmapConfig } from '../components/ReportWizard/SectionConfigHeatmap';
import type { KpiCardsConfig } from '../components/ReportWizard/SectionConfigKpiCards';
import type { BenchmarkCardsConfig } from '../components/ReportWizard/SectionConfigBenchmarkCards';
import type { BenchmarkRadarConfig } from '../components/ReportWizard/SectionConfigBenchmarkRadar';
import type { TableConfig } from '../components/ReportWizard/SectionConfigTable';
import type { LineConfig } from '../components/ReportWizard/SectionConfigLine';
import type { AreaHierarchyConfig } from '../components/ReportWizard/SectionConfigAreaHierarchy';
import type { DailyKpiHistoryConfig } from '../components/ReportWizard/SectionConfigDailyKpiHistory';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import '../style/Report.css';

const { Title, Text, Paragraph } = Typography;

interface AreaType {
    id: number;
    name: string;
    isGlobal?: boolean;
}

interface MetricDefinition {
    id: number;
    key: string;
    sourceType: string;
    sourceKey: string;
    name: string;
    aggregations?: string[];
    unit?: string | null;
}

interface AreaTypeMetric {
    id: number;
    areaTypeId: number;
    metricKey: string;
}

interface Pattern {
    id: number;
    name: string;
    patternType: string;
    areaType?: { id: number; name: string };
    area?: { id: number; name: string };
    isGlobal?: boolean;
}

type SectionVisual =
    | 'kpi-cards'
    | 'benchmark-cards'
    | 'benchmark-radar'
    | 'table'
    | 'daily-kpi-history'
    | 'line'
    | 'bar'
    | 'pattern-point-line'
    | 'heatmap'
    | 'area-hierarchy'
    | 'animated-heatmap'
    | 'animated-pattern-points'
    | 'pattern-list'
    | 'warning-card'
    | 'calendar-availability'
    | 'measurement-list'
    | 'measurement-info'
    | 'measurement-values'
    | 'measurement-texts'
    | 'measurement-weather'
    | 'measurement-location'
    | 'measurement-pattern-location'
    | 'measurement-files'
    | 'latest-analysis';

interface SectionRow {
    id: string;
    key: string;
    title: string;
    metrics: string[];
    visual: SectionVisual;
    span?: number;
    groupBy?: string;
    fields?: string[];
    aggregateChildren?: boolean;
    showChildBreakdown?: boolean;
    showParent?: boolean;
    showChildren?: boolean;
    cardSpan?: number;
    includeDescendants?: boolean;
    resolution?: string;
    chartType?: 'line' | 'bar';
    windowLookbackDays?: number | null;
    includeChildHeatmap?: boolean;
    childHeatmapDepth?: number | null;
    excludeParentHeatmap?: boolean;
    heatmapSpan?: number | null;
    childHeatmapSpan?: number | null;
    heatmapGridResolutionX?: number | null;
    heatmapGridResolutionY?: number | null;
    templateOpacity?: number | null;
    heatmapOpacity?: number | null;
    heatmapColorScheme?: string | null;
    heatmapValueRangeMode?: 'auto' | 'fixed' | 'areaDefault' | null;
    heatmapValueMin?: number | null;
    heatmapValueMax?: number | null;
    hexagonSizeMeters?: number | null;
    showMap?: boolean;
    // Animated Pattern Points properties
    circleSize?: number;
    colorScheme?: string;
    invertColors?: boolean | null;
    lookbackDays?: number;
    pointOpacity?: number;
    showLabels?: boolean;
    labelFontSize?: number;
    valueRangeMode?: 'auto' | 'fixed' | 'areaDefault';
    valueMin?: number | null;
    valueMax?: number | null;
    calendarShowCounts?: boolean;
    calendarShowLegend?: boolean;
    interpretTextAsJson?: boolean;
}

const formatAggregation = (agg: string) => ({
    label: { avg: 'Average', min: 'Min', max: 'Max', sum: 'Sum', count: 'Count',  median: 'Median', variance: 'Variance', stddev: 'Std Dev' }[agg] || agg,
    value: agg === 'avg' ? 'average' : agg,
});

const slugifyName = (input: string): string => {
    if (!input) return '';
    return input.toString().trim().toLowerCase().replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
};

interface WizardProps {
    editingTemplateId?: number;
    duplicateTemplateRequest?: { id: number; stamp: number };
    onSaved?: () => void;
}

const ReportTemplateWizard: React.FC<WizardProps> = ({ editingTemplateId, duplicateTemplateRequest, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [loadingTemplate, setLoadingTemplate] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saveForm] = Form.useForm();
    const [areaTypes, setAreaTypes] = useState<AreaType[]>([]);
    const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
    const [areaTypeMetrics, setAreaTypeMetrics] = useState<AreaTypeMetric[]>([]);
    const [patterns, setPatterns] = useState<Pattern[]>([]);
    const [patternPoints, setPatternPoints] = useState<any[]>([]);
    const [measurements, setMeasurements] = useState<any[]>([]);
    const [areas, setAreas] = useState<any[]>([]);
    const [previewAreaId, setPreviewAreaId] = useState<number | null>(null);
    const [previewPatternId, setPreviewPatternId] = useState<number | null>(null);
    const [previewPatternPointId, setPreviewPatternPointId] = useState<number | null>(null);
    const [previewPatternPoints, setPreviewPatternPoints] = useState<any[]>([]);
    const [loadingPatternPoints, setLoadingPatternPoints] = useState(false);
    const [previewMeasurementId, setPreviewMeasurementId] = useState<number | null>(null);
    const [previewDateRange, setPreviewDateRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(29, 'day'), dayjs()]);
    const [areaPatterns, setAreaPatterns] = useState<Pattern[]>([]);
    const [loadingPatterns, setLoadingPatterns] = useState(false);
    const [generatedTemplate, setGeneratedTemplate] = useState<any | null>(null);
    const [sections, setSections] = useState<SectionRow[]>([]);
    const [configsChanged, setConfigsChanged] = useState(false);
    const [isMaster, setIsMaster] = useState(false);
    const [companies, setCompanies] = useState<Array<{id: number, name: string}>>([]);
    const [templateState, setTemplateState] = useState({
        key: '',
        name: '',
        description: '',
        areaTypeIds: [] as number[],
        metricKeys: [] as string[], // Keep for sections compatibility
        metricIds: [] as number[],
        patternIds: [] as number[],
        pointIds: [] as number[],                    // NEW: for junction table
        measurementIds: [] as number[],              // NEW: for junction table
        lookbackDays: 30,
        includeDescendants: true,
        maxDepth: 2,
        isGlobal: false,
        companyId: undefined as number | undefined,
        accessibleFromAreaDropdown: true,
        accessibleFromMetricLinks: true,
        accessibleFromPatternLinks: true,
        accessibleFromPointLinks: true,              // NEW: access control
        accessibleFromMeasurementLinks: true,        // NEW: access control
    });

    // Load meta data
    useEffect(() => {
        const load = async () => {
            try {
                const requests = [
                    axios.get(ENDPOINTS.REPORT_TEMPLATE_WIZARD_META, { withCredentials: true }),
                    axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true, params: { include_archived: 'true' } })
                ];
                
                // Load companies for master users
                const [metaRes, areasRes, companiesRes] = await Promise.all([
                    ...requests,
                    axios.get(ENDPOINTS.GET_COMPANIES, { withCredentials: true }).catch(() => ({ data: [] }))
                ]);
                
                const data = metaRes.data?.data || metaRes.data || {};
                setAreaTypes(data.areaTypes || []);
                setMetrics(data.metrics || []);
                setAreaTypeMetrics(data.areaTypeMetrics || []);
                setPatterns(data.patterns || []);
                setPatternPoints(data.patternPoints || []);      // NEW: Load pattern points
                setMeasurements(data.measurements || []);        // NEW: Load measurements
                setAreas(areasRes.data?.data || areasRes.data || []);
                setCompanies(companiesRes.data?.data || companiesRes.data || []);
                
                // Get isMaster from meta response (much simpler!)
                setIsMaster(data.isMaster || false);
                
                console.log('👑 Is master user:', data.isMaster);
                console.log('📋 Loaded patterns:', data.patterns?.length || 0);
                console.log('📍 Loaded pattern points:', data.patternPoints?.length || 0);
                console.log('📊 Loaded measurements:', data.measurements?.length || 0);
            } catch (err: any) {
                console.error('❌ Failed to load wizard data:', err);
                // Don't show error message - data will just be empty
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // Load template for editing when editingTemplateId changes
    useEffect(() => {
        if (editingTemplateId) {
            loadTemplateForEdit(editingTemplateId);
            return;
        }

        if (duplicateTemplateRequest?.id) {
            loadTemplateForDuplicate(duplicateTemplateRequest.id);
            return;
        }

        // Reset wizard to initial state when not editing or duplicating
        setIsEditMode(false);
        setTemplateState({
            key: '',
            name: '',
            description: '',
            areaTypeIds: [],
            metricKeys: [],
            metricIds: [],
            patternIds: [],
            pointIds: [],
            measurementIds: [],
            lookbackDays: 30,
            includeDescendants: true,
            maxDepth: 2,
            isGlobal: false,
            companyId: undefined,
            accessibleFromAreaDropdown: true,
            accessibleFromMetricLinks: true,
            accessibleFromPatternLinks: true,
            accessibleFromPointLinks: true,
            accessibleFromMeasurementLinks: true,
        });
        setSections([]);
        setGeneratedTemplate(null);
        setConfigsChanged(false);
    }, [editingTemplateId, duplicateTemplateRequest]);

    const loadTemplateForEdit = async (templateId: number) => {
        setLoadingTemplate(true);
        try {
            const res = await axios.get(
                ENDPOINTS.REPORT_TEMPLATE_DETAIL(templateId),
                { withCredentials: true }
            );
            const template = res.data?.data || res.data;
            
            console.log('📥 Loading template for edit:', template);
            
            // Populate all wizard state from template
            setTemplateState({
                key: template.key,
                name: template.name,
                description: template.description || '',
                areaTypeIds: template.areaTypes?.map((at: any) => at.areaType.id) || [],
                metricKeys: template.metricKeys || [],
                metricIds: template.metrics?.map((m: any) => m.metric.id) || [],
                patternIds: template.patterns?.map((p: any) => p.pattern.id) || [],
                pointIds: template.patternPoints?.map((p: any) => p.patternPoint.id) || [],
                measurementIds: template.measurements?.map((m: any) => m.measurement.id) || [],
                lookbackDays: template.requiredInputs?.lookbackDays?.default || 30,
                includeDescendants: template.requiredInputs?.includeDescendants?.default ?? true,
                maxDepth: template.requiredInputs?.maxDepth?.default || 2,
                isGlobal: template.isGlobal || false,
                companyId: template.company?.id,
                accessibleFromAreaDropdown: template.accessibleFromAreaDropdown ?? true,
                accessibleFromMetricLinks: template.accessibleFromMetricLinks ?? true,
                accessibleFromPatternLinks: template.accessibleFromPatternLinks ?? true,
                accessibleFromPointLinks: template.accessibleFromPointLinks ?? true,
                accessibleFromMeasurementLinks: template.accessibleFromMeasurementLinks ?? true,
            });
            
            // Reconstruct sections from template
            const reconstructedSections = template.sections.map((sec: any, idx: number) => ({
                id: `section-${Date.now()}-${idx}`,
                key: sec.key,
                title: sec.title,
                metrics: sec.metrics || [],
                visual: sec.visual,
                span: sec.span || 24,
                fields: sec.fields || [],
                groupBy: sec.groupBy,
                aggregateChildren: sec.aggregateChildren ?? true,
                showChildBreakdown: sec.showChildBreakdown ?? true,
                showParent: sec.showParent ?? true,
                showChildren: sec.visual === 'benchmark-radar' ? (sec.showChildren ?? true) : (sec.showChildren ?? false),
                cardSpan: sec.cardSpan ?? 24,
                includeDescendants: sec.includeDescendants,
                resolution: sec.resolution || 'day',
                chartType: sec.chartType || 'line',
                windowLookbackDays: sec.windowLookbackDays,
                // Heatmap properties
                includeChildHeatmap: sec.childHeatmap?.include ?? false,
                childHeatmapDepth: sec.childHeatmap?.depth,
                excludeParentHeatmap: sec.childHeatmap?.excludeParent ?? false,
                heatmapSpan: sec.heatmapLayout?.span || 24,
                childHeatmapSpan: sec.heatmapLayout?.childSpan || 24,
                heatmapGridResolutionX: sec.heatmapGridResolutionX || 50,
                heatmapGridResolutionY: sec.heatmapGridResolutionY || null,
                hexagonSizeMeters: sec.hexagonSizeMeters || 15,
                templateOpacity: sec.templateOpacity || 70,
                heatmapOpacity: sec.heatmapOpacity || 90,
                heatmapColorScheme: sec.heatmapColorScheme || 'blue-red',
                heatmapValueRangeMode: sec.heatmapValueRangeMode || 'auto',
                heatmapValueMin: sec.heatmapValueMin,
                heatmapValueMax: sec.heatmapValueMax,
                showMap: sec.showMap ?? true,
                // Animated Pattern Points properties
                circleSize: sec.circleSize,
                colorScheme: sec.colorScheme,
                invertColors: sec.invertColors ?? false,
                lookbackDays: sec.lookbackDays,
                pointOpacity: sec.pointOpacity,
                showLabels: sec.showLabels,
                labelFontSize: sec.labelFontSize,
                valueRangeMode: sec.valueRangeMode,
                valueMin: sec.valueMin,
                valueMax: sec.valueMax,
                calendarShowCounts: sec.calendarShowCounts,
                calendarShowLegend: sec.calendarShowLegend,
                interpretTextAsJson: sec.interpretTextAsJson ?? true,
            }));
            
            setSections(reconstructedSections);
            setIsEditMode(true);
            setGeneratedTemplate(template); // Set as already generated
            
            message.success(`Loaded template: ${template.name}`);
        } catch (err: any) {
            message.error('Failed to load template for editing');
            console.error(err);
        } finally {
            setLoadingTemplate(false);
        }
    };

    const loadTemplateForDuplicate = async (templateId: number) => {
        setLoadingTemplate(true);
        try {
            const res = await axios.get(
                ENDPOINTS.REPORT_TEMPLATE_DETAIL(templateId),
                { withCredentials: true }
            );
            const template = res.data?.data || res.data;

            const duplicateName = `${template.name} (Copy)`;
            const suffix = Date.now().toString().slice(-4);
            const keyBase = template.key ? `${template.key}-copy` : slugifyName(duplicateName);
            const duplicateKey = slugifyName(`${keyBase}-${suffix}`);

            setTemplateState({
                key: duplicateKey,
                name: duplicateName,
                description: template.description || '',
                areaTypeIds: template.areaTypes?.map((at: any) => at.areaType.id) || [],
                metricKeys: template.metricKeys || [],
                metricIds: template.metrics?.map((m: any) => m.metric.id) || [],
                patternIds: template.patterns?.map((p: any) => p.pattern.id) || [],
                pointIds: template.patternPoints?.map((p: any) => p.patternPoint.id) || [],
                measurementIds: template.measurements?.map((m: any) => m.measurement.id) || [],
                lookbackDays: template.requiredInputs?.lookbackDays?.default || 30,
                includeDescendants: template.requiredInputs?.includeDescendants?.default ?? true,
                maxDepth: template.requiredInputs?.maxDepth?.default || 2,
                isGlobal: template.isGlobal || false,
                companyId: template.company?.id,
                accessibleFromAreaDropdown: template.accessibleFromAreaDropdown ?? true,
                accessibleFromMetricLinks: template.accessibleFromMetricLinks ?? true,
                accessibleFromPatternLinks: template.accessibleFromPatternLinks ?? true,
                accessibleFromPointLinks: template.accessibleFromPointLinks ?? true,
                accessibleFromMeasurementLinks: template.accessibleFromMeasurementLinks ?? true,
            });

            const reconstructedSections = template.sections.map((sec: any, idx: number) => ({
                id: `section-${Date.now()}-${idx}`,
                key: sec.key,
                title: sec.title,
                metrics: sec.metrics || [],
                visual: sec.visual,
                span: sec.span || 24,
                fields: sec.fields || [],
                groupBy: sec.groupBy,
                aggregateChildren: sec.aggregateChildren ?? true,
                showChildBreakdown: sec.showChildBreakdown ?? true,
                showParent: sec.showParent ?? true,
                showChildren: sec.visual === 'benchmark-radar' ? (sec.showChildren ?? true) : (sec.showChildren ?? false),
                cardSpan: sec.cardSpan ?? 24,
                includeDescendants: sec.includeDescendants,
                resolution: sec.resolution || 'day',
                chartType: sec.chartType || 'line',
                windowLookbackDays: sec.windowLookbackDays,
                includeChildHeatmap: sec.childHeatmap?.include ?? false,
                childHeatmapDepth: sec.childHeatmap?.depth,
                excludeParentHeatmap: sec.childHeatmap?.excludeParent ?? false,
                heatmapSpan: sec.heatmapLayout?.span || 24,
                childHeatmapSpan: sec.heatmapLayout?.childSpan || 24,
                heatmapGridResolutionX: sec.heatmapGridResolutionX || 50,
                heatmapGridResolutionY: sec.heatmapGridResolutionY || null,
                hexagonSizeMeters: sec.hexagonSizeMeters || 15,
                templateOpacity: sec.templateOpacity || 70,
                heatmapOpacity: sec.heatmapOpacity || 90,
                heatmapColorScheme: sec.heatmapColorScheme || 'blue-red',
                heatmapValueRangeMode: sec.heatmapValueRangeMode || 'auto',
                heatmapValueMin: sec.heatmapValueMin,
                heatmapValueMax: sec.heatmapValueMax,
                showMap: sec.showMap ?? true,
                circleSize: sec.circleSize,
                colorScheme: sec.colorScheme,
                invertColors: sec.invertColors ?? false,
                lookbackDays: sec.lookbackDays,
                pointOpacity: sec.pointOpacity,
                showLabels: sec.showLabels,
                labelFontSize: sec.labelFontSize,
                valueRangeMode: sec.valueRangeMode,
                valueMin: sec.valueMin,
                valueMax: sec.valueMax,
                calendarShowCounts: sec.calendarShowCounts,
                calendarShowLegend: sec.calendarShowLegend,
                interpretTextAsJson: sec.interpretTextAsJson ?? true,
            }));

            setSections(reconstructedSections);
            setIsEditMode(false);
            setGeneratedTemplate(template);
            setConfigsChanged(false);

            message.success(`Duplicating template: ${template.name}`);
        } catch (err: any) {
            message.error('Failed to load template for duplication');
            console.error(err);
        } finally {
            setLoadingTemplate(false);
        }
    };

    // Initialize with one section
    useEffect(() => {
        if (sections.length === 0) {
            setSections([{
                id: `section-${Date.now()}`,
                key: `section-${Date.now()}`,
                title: '',
                metrics: [],
                visual: 'kpi-cards',
                fields: [],
                aggregateChildren: true,
                showChildBreakdown: true,
                showParent: true,
                showChildren: false,
                cardSpan: 24,
                resolution: 'day',
                chartType: 'line',
                windowLookbackDays: null,
                includeChildHeatmap: false,
                childHeatmapDepth: null,
                excludeParentHeatmap: false,
                heatmapSpan: 24,
                childHeatmapSpan: 24,
                heatmapGridResolutionX: 50,
                heatmapGridResolutionY: null,
                templateOpacity: 70,
                heatmapOpacity: 90,
                heatmapColorScheme: 'blue-red',
                invertColors: false,
                heatmapValueRangeMode: 'auto',
                heatmapValueMin: null,
                heatmapValueMax: null,
                hexagonSizeMeters: 15,
                calendarShowCounts: true,
                calendarShowLegend: true,
            }]);
        }
    }, [sections.length]);

    // Mark as changed when configs update (but NOT when template is generated)
    useEffect(() => {
        if (generatedTemplate) setConfigsChanged(true);
    }, [sections, templateState]); // Removed generatedTemplate from deps - only track actual config changes

    // Set initial preview area
    const filteredAreas = useMemo(() => {
        if (!templateState.areaTypeIds.length) return areas;
        return areas.filter((a: any) => templateState.areaTypeIds.includes(a.areaType?.id));
    }, [areas, templateState.areaTypeIds]);

    useEffect(() => {
        if (filteredAreas.length > 0 && !previewAreaId) {
            setPreviewAreaId(filteredAreas[0].id);
        }
    }, [filteredAreas, previewAreaId]);

    // Dynamically fetch patterns when area changes
    useEffect(() => {
        const fetchAreaPatterns = async () => {
            if (!previewAreaId) {
                setAreaPatterns([]);
                setPreviewPatternId(null);
                setPreviewPatternPointId(null);
                setPreviewPatternPoints([]);
                return;
            }

            setLoadingPatterns(true);
            try {
                const res = await axios.get(
                    ENDPOINTS.AREA_PATTERNS(previewAreaId),
                    { withCredentials: true, params: { include_archived: 'true' } }
                );
                const patternsData = res.data?.data || res.data || [];
                setAreaPatterns(patternsData.slice(0, 10)); // Limit to 10 for performance
                
                // Auto-select first pattern
                if (patternsData.length > 0) {
                    setPreviewPatternId(patternsData[0].id);
                } else {
                    setPreviewPatternId(null);
                    setPreviewPatternPointId(null);
                    setPreviewPatternPoints([]);
                }
            } catch (err) {
                console.error('Failed to fetch area patterns:', err);
                setAreaPatterns([]);
                setPreviewPatternId(null);
                setPreviewPatternPointId(null);
                setPreviewPatternPoints([]);
            } finally {
                setLoadingPatterns(false);
            }
        };

        fetchAreaPatterns();
    }, [previewAreaId]);

    useEffect(() => {
        const fetchPatternPoints = async () => {
            if (!previewPatternId) {
                setPreviewPatternPoints([]);
                setPreviewPatternPointId(null);
                return;
            }

            setLoadingPatternPoints(true);
            setPreviewPatternPointId(null);
            try {
                const res = await axios.get(
                    ENDPOINTS.PATTERN_POINTS(previewPatternId),
                    { withCredentials: true }
                );
                const points = res.data?.data || res.data || [];
                setPreviewPatternPoints(points);
            } catch (err) {
                console.error('Failed to fetch pattern points:', err);
                setPreviewPatternPoints([]);
                setPreviewPatternPointId(null);
            } finally {
                setLoadingPatternPoints(false);
            }
        };

        fetchPatternPoints();
    }, [previewPatternId]);

    const needsPatternSelection = useMemo(
        () => sections.some(section => section.visual === 'pattern-point-line' || section.visual === 'measurement-list'),
        [sections]
    );
    const showPatternSelector = needsPatternSelection
        || templateState.accessibleFromPatternLinks
        || templateState.accessibleFromPointLinks;
    const needsMeasurementSelection = useMemo(
        () => sections.some(section => ['measurement-info', 'measurement-values', 'measurement-texts', 'measurement-weather', 'measurement-location', 'measurement-pattern-location', 'measurement-files'].includes(section.visual)),
        [sections]
    );
    const showMeasurementSelector = needsMeasurementSelection || templateState.accessibleFromMeasurementLinks;
    const filteredMeasurements = useMemo(() => {
        if (!previewAreaId) return measurements;
        return measurements.filter((m: any) => m?.area?.id === previewAreaId || m?.areaId === previewAreaId);
    }, [measurements, previewAreaId]);

    useEffect(() => {
        if (!showMeasurementSelector) {
            setPreviewMeasurementId(null);
            return;
        }
        if (filteredMeasurements.length === 0) {
            setPreviewMeasurementId(null);
            return;
        }
        const exists = previewMeasurementId
            ? filteredMeasurements.some((m: any) => m.id === previewMeasurementId)
            : false;
        if (!exists) {
            setPreviewMeasurementId(filteredMeasurements[0].id);
        }
    }, [filteredMeasurements, previewMeasurementId, showMeasurementSelector]);

    const getFieldOptions = useCallback((sectionMetrics: string[]) => {
        if (!sectionMetrics.length) return [];
        const aggs = new Set<string>();
        sectionMetrics.forEach(mk => {
            const m = metrics.find(m => m.key === mk);
            if (m?.aggregations) m.aggregations.forEach(a => aggs.add(a));
        });
        return Array.from(aggs).map(formatAggregation);
    }, [metrics]);

    const handleGenerate = async () => {
        if (!templateState.key || !templateState.name || !sections.length) {
            message.warning('Please complete template configuration');
            return;
        }
        
        setGenerating(true);
        try {
            const allMetrics = new Set(templateState.metricKeys);
            sections.forEach(s => s.metrics.forEach(m => allMetrics.add(m)));
            
            const payload = {
                ...templateState,
                metricKeys: Array.from(allMetrics),
                sections: sections.map(s => {
                    const sec: any = { key: s.key, title: s.title, metrics: s.metrics, visual: s.visual };
                    if (s.span !== undefined) sec.span = s.span;
                    if (s.includeDescendants !== undefined) sec.includeDescendants = s.includeDescendants;
                    if (s.visual === 'heatmap' || s.visual === 'animated-heatmap') {
                        if (s.visual === 'heatmap') {
                            sec.childHeatmap = {
                                include: !!s.includeChildHeatmap,
                                depth: s.childHeatmapDepth,
                                excludeParent: !!s.excludeParentHeatmap,
                            };
                            sec.heatmapLayout = { span: s.heatmapSpan, childSpan: s.childHeatmapSpan };
                        }
                        // Common heatmap properties for both visual types
                        sec.heatmapGridResolutionX = s.heatmapGridResolutionX;
                        sec.heatmapGridResolutionY = s.heatmapGridResolutionY;
                        sec.hexagonSizeMeters = s.hexagonSizeMeters;
                        sec.templateOpacity = s.templateOpacity;
                        sec.heatmapOpacity = s.heatmapOpacity;
                        sec.heatmapColorScheme = s.heatmapColorScheme;
                        sec.invertColors = s.invertColors;
                        sec.heatmapValueRangeMode = s.heatmapValueRangeMode;
                        sec.heatmapValueMin = s.heatmapValueMin;
                        sec.heatmapValueMax = s.heatmapValueMax;
                        if (s.resolution) sec.resolution = s.resolution;
                    } else if (s.visual === 'animated-pattern-points') {
                        // Pattern points specific properties
                        sec.circleSize = s.circleSize;
                        sec.colorScheme = s.colorScheme;
                        sec.invertColors = s.invertColors;
                        sec.pointOpacity = s.pointOpacity;
                        sec.templateOpacity = s.templateOpacity;
                        sec.showLabels = s.showLabels;
                        sec.labelFontSize = s.labelFontSize;
                        sec.valueRangeMode = s.valueRangeMode;
                        sec.valueMin = s.valueMin;
                        sec.valueMax = s.valueMax;
                        sec.lookbackDays = s.lookbackDays;
                        if (s.resolution) sec.resolution = s.resolution;
                    } else if (s.visual === 'calendar-availability') {
                        sec.calendarShowCounts = s.calendarShowCounts ?? true;
                        sec.calendarShowLegend = s.calendarShowLegend ?? true;
                    } else if (s.visual === 'measurement-texts') {
                        sec.interpretTextAsJson = s.interpretTextAsJson ?? true;
                    } else {
                        sec.aggregateChildren = s.aggregateChildren;
                    }
                    if (s.fields?.length) sec.fields = s.fields;
                    if (s.groupBy) sec.groupBy = s.groupBy;
                    if (s.resolution) sec.resolution = s.resolution;
                    if (s.visual === 'daily-kpi-history' && s.chartType) sec.chartType = s.chartType;
                    if (s.showParent !== undefined) sec.showParent = s.showParent;
                    if (s.showChildren !== undefined) sec.showChildren = s.showChildren;
                    if (s.cardSpan !== undefined) sec.cardSpan = s.cardSpan;
                    if (s.showMap !== undefined) sec.showMap = s.showMap;
                    return sec;
                }),
            };
            
            const res = await axios.post(ENDPOINTS.REPORT_TEMPLATE_WIZARD_GENERATE, payload, { withCredentials: true });
            setGeneratedTemplate(res.data?.data || res.data);
            setConfigsChanged(false);
            message.success('Template generated!');
        } catch (err) {
            message.error('Failed to generate template');
        } finally {
            setGenerating(false);
        }
    };

    const addSection = () => {
        setSections(prev => [...prev, {
            id: `section-${Date.now()}`,
            key: `section-${Date.now()}`,
            title: '',
            metrics: [],
            visual: 'kpi-cards',
            fields: [],
            aggregateChildren: true,
            showChildBreakdown: true,
            showParent: true,
            showChildren: false,
            cardSpan: 24,
            resolution: 'day',
            chartType: 'line',
            windowLookbackDays: null,
            includeChildHeatmap: false,
            childHeatmapDepth: null,
            excludeParentHeatmap: false,
            heatmapSpan: 24,
            childHeatmapSpan: 24,
            heatmapGridResolutionX: 50,
            heatmapGridResolutionY: null,
            templateOpacity: 70,
            heatmapOpacity: 90,
            heatmapColorScheme: 'blue-red',
            invertColors: false,
            heatmapValueRangeMode: 'auto',
            heatmapValueMin: null,
            heatmapValueMax: null,
            hexagonSizeMeters: 15,
            calendarShowCounts: true,
            calendarShowLegend: true,
        }]);
    };

    const handleSaveTemplate = async () => {
        if (!generatedTemplate) {
            message.warning('Please generate template first');
            return;
        }

        // Prepopulate form with current values (preserves scope when editing)
        saveForm.setFieldsValue({
            name: templateState.name,
            description: templateState.description,
            areaTypeIds: templateState.areaTypeIds || [],
            isGlobal: templateState.isGlobal, // Preserve the actual value from template
        });
        
        setShowSaveModal(true);
    };

    const handleSaveSubmit = async () => {
        try {
            const values = await saveForm.validateFields();
            setSaving(true);

            const allMetrics = new Set(templateState.metricKeys);
            sections.forEach(s => s.metrics.forEach(m => allMetrics.add(m)));

            const payload: any = {
                key: templateState.key,
                name: values.name,
                description: values.description,
                scope: 'area',
                areaTypeIds: values.areaTypeIds || [],
                metricIds: templateState.metricIds || [],
                patternIds: templateState.patternIds || [],
                pointIds: templateState.pointIds || [],                                  // NEW
                measurementIds: templateState.measurementIds || [],                      // NEW
                accessibleFromAreaDropdown: templateState.accessibleFromAreaDropdown,
                accessibleFromMetricLinks: templateState.accessibleFromMetricLinks,
                accessibleFromPatternLinks: templateState.accessibleFromPatternLinks,
                accessibleFromPointLinks: templateState.accessibleFromPointLinks,        // NEW
                accessibleFromMeasurementLinks: templateState.accessibleFromMeasurementLinks,  // NEW
                isGlobal: templateState.isGlobal,
                sections: generatedTemplate.sections,
                metricKeys: Array.from(allMetrics),
                requiredInputs: generatedTemplate.requiredInputs,
                metadata: generatedTemplate.metadata,
                status: 'active',
            };
            
            // Add companyId for company-specific templates (master users only)
            if (!templateState.isGlobal && templateState.companyId) {
                payload.companyId = templateState.companyId;
            }

            let response;
            if (isEditMode && editingTemplateId) {
                // UPDATE existing template
                console.log('📝 Updating template with payload:', payload);
                response = await axios.put(
                    ENDPOINTS.REPORT_TEMPLATE_DETAIL(editingTemplateId),
                    payload,
                    { withCredentials: true }
                );
                console.log('✅ Template updated successfully:', response.data);
                message.success('Template updated successfully!');
            } else {
                // CREATE new template
                console.log('💾 Creating template with payload:', payload);
                response = await axios.post(
                    ENDPOINTS.REPORT_TEMPLATES,
                    payload,
                    { withCredentials: true }
                );
                console.log('✅ Template created successfully:', response.data);
                message.success('Template created successfully!');
            }
            
            setShowSaveModal(false);
            saveForm.resetFields();
            
            // Call onSaved callback if provided
            if (onSaved) {
                onSaved();
            }
        } catch (err: any) {
            console.error('❌ Save template error:', {
                status: err.response?.status,
                message: err.response?.data?.message,
                error: err.response?.data,
                fullError: err
            });
            message.error(err.response?.data?.message || `Failed to save template: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const previewTemplate = useMemo(() => {
        if (!generatedTemplate) return null;
        return {
            ...generatedTemplate,
            sections: generatedTemplate.sections.map((sec: any) => ({
                ...sec,
                patternId: (sec.visual === 'pattern-point-line' || sec.visual === 'measurement-list') && previewPatternId
                    ? previewPatternId
                    : sec.patternId,
                patternPointId: (sec.visual === 'pattern-point-line' || sec.visual === 'measurement-list') && previewPatternPointId
                    ? previewPatternPointId
                    : sec.patternPointId,
                measurementId: ['measurement-info', 'measurement-values', 'measurement-texts', 'measurement-weather', 'measurement-location', 'measurement-pattern-location', 'measurement-files', 'latest-analysis'].includes(sec.visual) && previewMeasurementId
                    ? previewMeasurementId
                    : sec.measurementId,
            })),
        };
    }, [generatedTemplate, previewPatternId, previewPatternPointId]);

    const previewDateFrom = useMemo(
        () => previewDateRange[0].toISOString(),
        [previewDateRange]
    );
    const previewDateTo = useMemo(
        () => previewDateRange[1].toISOString(),
        [previewDateRange]
    );

    if (loading || loadingTemplate) {
        return <Spin fullscreen tip={loadingTemplate ? "Loading template..." : "Loading..."} />;
    }

    const metricOpts = metrics.map(m => ({ label: `${m.key} (${m.sourceType})`, value: m.key }));
    const metricIdOpts = metrics.map(m => ({ label: `${m.name} (${m.key})`, value: m.id }));
    const areaTypeOpts = areaTypes.map(at => ({ label: at.name + (at.isGlobal ? ' (global)' : ''), value: at.id }));
    const patternOpts = patterns.map(p => ({ 
        label: `${p.name} (${p.patternType})${p.isGlobal ? ' [global]' : ''}`, 
        value: p.id 
    }));

    return (
        <div className="report-wizard-page">
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <div className="report-wizard-header">
                        <Title level={3} style={{ margin: 0 }}>
                            {isEditMode ? '✏️ Edit Report Template' : 'Report Template Wizard'}
                        </Title>
                        <Paragraph className="report-wizard-subtitle">
                            {isEditMode
                                ? 'Update the template configuration and sections below.'
                                : 'Create report templates by configuring sections. Preview uses the Report component.'}
                        </Paragraph>
                    </div>
                </Col>

                {/* Template Config */}
                <Col span={24}>
                    <Card title="Template Configuration">
                        <Form layout="vertical">
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item label="Name" required>
                                        <Input value={templateState.name} onChange={e => {
                                            const name = e.target.value;
                                            const slug = slugifyName(templateState.name || '');
                                            setTemplateState(prev => ({
                                                ...prev,
                                                name,
                                                key: !prev.key || prev.key === slug ? slugifyName(name) : prev.key
                                            }));
                                        }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Key" required>
                                        <Input value={templateState.key} onChange={e => setTemplateState(prev => ({ ...prev, key: e.target.value }))} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Description">
                                <Input.TextArea rows={2} value={templateState.description} onChange={e => setTemplateState(prev => ({ ...prev, description: e.target.value }))} />
                            </Form.Item>
                            <Row gutter={12}>
                                <Col span={8}>
                                    <Form.Item label="Area Types" help="Link to specific area types">
                                        <Select 
                                            mode="multiple" 
                                            options={areaTypeOpts} 
                                            value={templateState.areaTypeIds} 
                                            onChange={v => setTemplateState(prev => ({ ...prev, areaTypeIds: v }))}
                                            placeholder="Select area types"
                                            showSearch
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Linked Metrics" help="Link to specific metrics">
                                        <Select 
                                            mode="multiple" 
                                            showSearch 
                                            options={metricIdOpts} 
                                            value={templateState.metricIds} 
                                            onChange={v => setTemplateState(prev => ({ ...prev, metricIds: v }))}
                                            placeholder="Select metrics"
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Linked Patterns" help="Link to specific patterns">
                                        <Select 
                                            mode="multiple" 
                                            showSearch 
                                            options={patternOpts} 
                                            value={templateState.patternIds} 
                                            onChange={v => setTemplateState(prev => ({ ...prev, patternIds: v }))}
                                            placeholder="Select patterns"
                                            allowClear
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={24}>
                                    <Form.Item label="Section Metrics" help="Metrics used in report sections">
                                        <Select 
                                            mode="multiple" 
                                            showSearch 
                                            options={metricOpts} 
                                            value={templateState.metricKeys} 
                                            onChange={v => setTemplateState(prev => ({ ...prev, metricKeys: v }))}
                                            placeholder="Select metrics for sections"
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={8}>
                                    <Form.Item label="Lookback Days">
                                        <InputNumber min={1} style={{ width: '100%' }} value={templateState.lookbackDays} onChange={v => setTemplateState(prev => ({ ...prev, lookbackDays: v || 30 }))} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Max Depth">
                                        <InputNumber min={0} style={{ width: '100%' }} value={templateState.maxDepth} onChange={v => setTemplateState(prev => ({ ...prev, maxDepth: v || 0 }))} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <Form.Item label="Include Descendants">
                                        <Switch checked={templateState.includeDescendants} onChange={v => setTemplateState(prev => ({ ...prev, includeDescendants: v }))} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            {isMaster && (
                                <>
                                    <Row gutter={12}>
                                        <Col span={24}>
                                            <Form.Item 
                                                label="Template Scope" 
                                                help="Global templates are visible to all companies system-wide"
                                            >
                                                <Switch 
                                                    checked={templateState.isGlobal} 
                                                    onChange={v => setTemplateState(prev => ({ ...prev, isGlobal: v, companyId: v ? undefined : prev.companyId }))}
                                                    checkedChildren="Global" 
                                                    unCheckedChildren="Company"
                                                />
                                                {templateState.isGlobal ? (
                                                    <Tag color="blue" style={{ marginLeft: 12 }}>Visible to all companies</Tag>
                                                ) : (
                                                    <Tag style={{ marginLeft: 12 }}>Company-specific</Tag>
                                                )}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {!templateState.isGlobal && (
                                        <Row gutter={12}>
                                            <Col span={24}>
                                                <Form.Item 
                                                    label="Assign to Company"
                                                    help="Select which company will have access to this template"
                                                >
                                                    <Select
                                                        placeholder="Select company"
                                                        value={templateState.companyId}
                                                        onChange={v => setTemplateState(prev => ({ ...prev, companyId: v }))}
                                                        options={companies.map(c => ({ label: c.name, value: c.id }))}
                                                        showSearch
                                                        allowClear
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )}
                                </>
                            )}
                        </Form>
                    </Card>
                </Col>

                {/* Template Access Control */}
                <Col span={24}>
                    <Card title="Template Access Control" size="small">
                        <Form layout="vertical">
                            <Form.Item 
                                label="Where should this template be accessible?"
                                help="Control which navigation contexts will show this template"
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                        <Switch 
                                            checked={templateState.accessibleFromAreaDropdown}
                                            onChange={v => setTemplateState(prev => ({ 
                                                ...prev, 
                                                accessibleFromAreaDropdown: v 
                                            }))}
                                            style={{ marginRight: 12 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <Text strong>📍 Area Detail Page</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Show in report dropdown when viewing area pages
                                            </Text>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                        <Switch 
                                            checked={templateState.accessibleFromMetricLinks}
                                            onChange={v => setTemplateState(prev => ({ 
                                                ...prev, 
                                                accessibleFromMetricLinks: v 
                                            }))}
                                            style={{ marginRight: 12 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <Text strong>📊 Metric Links</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Show when users click metric names in reports
                                            </Text>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                        <Switch 
                                            checked={templateState.accessibleFromPatternLinks}
                                            onChange={v => setTemplateState(prev => ({ 
                                                ...prev, 
                                                accessibleFromPatternLinks: v 
                                            }))}
                                            style={{ marginRight: 12 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <Text strong>🎯 Pattern Pages</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Show on pattern detail pages
                                            </Text>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                        <Switch 
                                            checked={templateState.accessibleFromPointLinks}
                                            onChange={v => setTemplateState(prev => ({ 
                                                ...prev, 
                                                accessibleFromPointLinks: v 
                                            }))}
                                            style={{ marginRight: 12 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <Text strong>📍 Pattern Point Reports</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Enable for point-specific report generation (filtered by area type, metric, etc.)
                                            </Text>
                                        </div>
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
                                        <Switch 
                                            checked={templateState.accessibleFromMeasurementLinks}
                                            onChange={v => setTemplateState(prev => ({ 
                                                ...prev, 
                                                accessibleFromMeasurementLinks: v 
                                            }))}
                                            style={{ marginRight: 12 }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <Text strong>📊 Measurement Reports</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Enable for individual measurement analysis and reporting
                                            </Text>
                                        </div>
                                    </div>
                                </Space>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>

                {/* Sections Config */}
                <Col span={24}>
                    <Card title="Sections">
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                            {sections.map((section, idx) => (
                                <Card key={section.id} size="small" title={section.title || section.key} extra={
                                    <Space>
                                        <Button size="small" icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => {
                                            const arr = [...sections];
                                            [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
                                            setSections(arr);
                                        }} />
                                        <Button size="small" icon={<ArrowDownOutlined />} disabled={idx === sections.length - 1} onClick={() => {
                                            const arr = [...sections];
                                            [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
                                            setSections(arr);
                                        }} />
                                        <Button danger size="small" onClick={() => setSections(prev => prev.filter(s => s.id !== section.id))}>Remove</Button>
                                    </Space>
                                }>
                                    <Row gutter={12}>
                                        <Col span={8}>
                                            <Form.Item label="Title">
                                                <Input value={section.title} onChange={e => setSections(prev => prev.map(s => s.id === section.id ? { ...s, title: e.target.value } : s))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={8}>
                                            <Form.Item label="Key">
                                                <Input value={section.key} onChange={e => setSections(prev => prev.map(s => s.id === section.id ? { ...s, key: e.target.value } : s))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item label="Visual Type">
                                                <Select value={section.visual} onChange={v => setSections(prev => prev.map(s => {
                                                    if (s.id !== section.id) return s;
                                                    const next: SectionRow = { ...s, visual: v };
                                                    if (v === 'benchmark-radar' && s.visual !== 'benchmark-radar') {
                                                        next.showChildren = true;
                                                    }
                                                    if (v === 'daily-kpi-history' && s.visual !== 'daily-kpi-history') {
                                                        next.chartType = 'line';
                                                        next.showParent = true;
                                                        next.showChildren = false;
                                                    }
                                                    if (v === 'measurement-texts' && s.visual !== 'measurement-texts') {
                                                        next.interpretTextAsJson = true;
                                                    }
                                                    return next;
                                                }))} options={[
                                                    { label: 'KPI Cards', value: 'kpi-cards' },
                                                    { label: 'Benchmark Cards', value: 'benchmark-cards' },
                                                    { label: 'Benchmark Radar', value: 'benchmark-radar' },
                                                    { label: 'Warning Card', value: 'warning-card' },
                                                    { label: 'Table', value: 'table' },
                                                    { label: 'Daily KPI History', value: 'daily-kpi-history' },
                                                    { label: 'Line Chart', value: 'line' },
                                                    { label: 'Bar Chart', value: 'bar' },
                                                    { label: 'Pattern Point Line Chart', value: 'pattern-point-line' },
                                                    { label: 'Heatmap', value: 'heatmap' },
                                                    { label: 'Animated Heatmap', value: 'animated-heatmap' },
                                                    { label: 'Animated Pattern Points', value: 'animated-pattern-points' },
                                                    { label: 'Area Hierarchy', value: 'area-hierarchy' },
                                                    { label: 'Pattern List', value: 'pattern-list' },
                                                    { label: 'List of Measurements', value: 'measurement-list' },
                                                    { label: 'Measurement Info', value: 'measurement-info' },
                                                    { label: 'Measurement Values', value: 'measurement-values' },
                                                    { label: 'Measurement Notes', value: 'measurement-texts' },
                                                    { label: 'Measurement Weather', value: 'measurement-weather' },
                                                    { label: 'Measurement Location (GPS)', value: 'measurement-location' },
                                                    { label: 'Measurement Pattern Location', value: 'measurement-pattern-location' },
                                                    { label: 'Measurement Files', value: 'measurement-files' },
                                                    { label: 'Latest AI Analysis', value: 'latest-analysis' },
                                                    { label: 'Calendar Availability', value: 'calendar-availability' },
                                                ]} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item label="Width (span)" help="1-24 columns">
                                                <InputNumber
                                                    min={1}
                                                    max={24}
                                                    value={section.span || 24}
                                                    onChange={v => setSections(prev => prev.map(s => s.id === section.id ? { ...s, span: v || 24 } : s))}
                                                    style={{ width: '100%' }}
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    {/* Only show metrics for visual types that need them */}
                                    {!['area-hierarchy', 'pattern-list', 'calendar-availability', 'measurement-list', 'measurement-info', 'measurement-values', 'measurement-texts', 'measurement-weather', 'measurement-location', 'measurement-pattern-location', 'measurement-files', 'latest-analysis'].includes(section.visual) && (
                                        <Form.Item label="Metrics">
                                            <Select mode="multiple" options={metricOpts} value={section.metrics} onChange={v => setSections(prev => prev.map(s => s.id === section.id ? { ...s, metrics: v } : s))} />
                                        </Form.Item>
                                    )}

                                    {section.visual === 'kpi-cards' && (
                                        <SectionConfigKpiCards
                                            initialConfig={{ fields: section.fields || [], aggregateChildren: section.aggregateChildren ?? true, showChildBreakdown: section.showChildBreakdown ?? true }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: KpiCardsConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'benchmark-cards' && (
                                        <SectionConfigBenchmarkCards
                                            initialConfig={{
                                                fields: section.fields || ['average'],
                                                showParent: section.showParent ?? true,
                                                cardSpan: section.cardSpan ?? 24,
                                            }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: BenchmarkCardsConfig) => setSections(prev => prev.map(s => s.id === section.id ? {
                                                ...s,
                                                fields: c.fields,
                                                showParent: c.showParent,
                                                cardSpan: c.cardSpan,
                                            } : s))}
                                        />
                                    )}
                                    {section.visual === 'benchmark-radar' && (
                                        <SectionConfigBenchmarkRadar
                                            initialConfig={{
                                                fields: section.fields || ['average'],
                                                showChildren: section.showChildren ?? true,
                                            }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: BenchmarkRadarConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, fields: c.fields, showChildren: c.showChildren } : s))}
                                        />
                                    )}
                                    {section.visual === 'warning-card' && (
                                        <SectionConfigWarningCard
                                            initialConfig={{ fields: section.fields || [] }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: { fields: string[] }) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'table' && (
                                        <SectionConfigTable
                                            initialConfig={{ fields: section.fields || [], groupBy: section.groupBy || null, aggregateChildren: section.aggregateChildren ?? true }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: TableConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, fields: c.fields, groupBy: c.groupBy ?? undefined, aggregateChildren: c.aggregateChildren } : s))}
                                        />
                                    )}
                                    {section.visual === 'daily-kpi-history' && (
                                        <SectionConfigDailyKpiHistory
                                            initialConfig={{
                                                chartType: section.chartType || 'line',
                                                showParent: section.showParent ?? true,
                                                showChildren: section.showChildren ?? false,
                                            }}
                                            onChange={(c: DailyKpiHistoryConfig) =>
                                                setSections(prev =>
                                                    prev.map(s => (s.id === section.id ? { ...s, ...c } : s))
                                                )
                                            }
                                        />
                                    )}
                                    {(section.visual === 'line' || section.visual === 'bar') && (
                                        <SectionConfigLine
                                            initialConfig={{ fields: section.fields || [], resolution: section.resolution || 'day', showParent: section.showParent ?? true, showChildren: section.showChildren ?? false }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: LineConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'pattern-point-line' && (
                                        <SectionConfigPatternPointLine
                                            initialConfig={{ fields: section.fields || [], resolution: section.resolution || 'day', showParent: section.showParent ?? true, showPoints: section.showChildren ?? false }}
                                            fieldOptions={getFieldOptions(section.metrics)}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c, showChildren: c.showPoints } : s))}
                                        />
                                    )}
                                    {section.visual === 'heatmap' && (
                                        <SectionConfigHeatmap
                                            initialConfig={{
                                                includeChildHeatmap: section.includeChildHeatmap ?? false,
                                                excludeParentHeatmap: section.excludeParentHeatmap ?? false,
                                                childHeatmapDepth: section.childHeatmapDepth,
                                                heatmapSpan: section.heatmapSpan,
                                                childHeatmapSpan: section.childHeatmapSpan,
                                                heatmapGridResolutionX: section.heatmapGridResolutionX,
                                                heatmapGridResolutionY: section.heatmapGridResolutionY,
                                                hexagonSizeMeters: section.hexagonSizeMeters,
                                                templateOpacity: section.templateOpacity,
                                                heatmapOpacity: section.heatmapOpacity,
                                                heatmapColorScheme: section.heatmapColorScheme,
                                                invertColors: section.invertColors ?? false,
                                                heatmapValueRangeMode: section.heatmapValueRangeMode,
                                                heatmapValueMin: section.heatmapValueMin,
                                                heatmapValueMax: section.heatmapValueMax,
                                            }}
                                            onChange={(c: HeatmapConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'animated-heatmap' && (
                                        <SectionConfigAnimatedHeatmap
                                            initialConfig={{
                                                resolution: section.resolution,
                                                heatmapGridResolutionX: section.heatmapGridResolutionX,
                                                heatmapGridResolutionY: section.heatmapGridResolutionY,
                                                hexagonSizeMeters: section.hexagonSizeMeters,
                                                templateOpacity: section.templateOpacity,
                                                heatmapOpacity: section.heatmapOpacity,
                                                heatmapColorScheme: section.heatmapColorScheme,
                                                invertColors: section.invertColors ?? false,
                                                heatmapValueRangeMode: section.heatmapValueRangeMode,
                                                heatmapValueMin: section.heatmapValueMin,
                                                heatmapValueMax: section.heatmapValueMax,
                                            }}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'animated-pattern-points' && (
                                        <SectionConfigAnimatedPatternPoints
                                            initialConfig={{
                                                circleSize: section.circleSize,
                                                colorScheme: section.colorScheme,
                                                invertColors: section.invertColors ?? false,
                                                resolution: section.resolution as 'hour' | 'day' | 'week' | 'month' | undefined,
                                                lookbackDays: section.lookbackDays,
                                                pointOpacity: section.pointOpacity,
                                                templateOpacity: section.templateOpacity ?? undefined,
                                                showLabels: section.showLabels,
                                                labelFontSize: section.labelFontSize,
                                                valueRangeMode: section.valueRangeMode || 'auto',
                                                valueMin: section.valueMin ?? undefined,
                                                valueMax: section.valueMax ?? undefined,
                                            }}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'area-hierarchy' && (
                                        <SectionConfigAreaHierarchy
                                            initialConfig={{ showMap: section.showMap ?? true }}
                                            onChange={(c: AreaHierarchyConfig) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'pattern-list' && (
                                        <SectionConfigPatternList
                                            initialConfig={{}}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'measurement-list' && (
                                        <SectionConfigMeasurementList
                                            initialConfig={{}}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                    {section.visual === 'measurement-info' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Info"
                                            description="Shows measurement metadata, timestamps, device, area hierarchy, pattern, pattern point, mission, and mission point."
                                        />
                                    )}
                                    {section.visual === 'measurement-values' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Values"
                                            description="Displays all recorded values for the selected measurement."
                                        />
                                    )}
                                    {section.visual === 'measurement-texts' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Notes"
                                            description="Displays notes, comments, and device metadata attached to the measurement."
                                            interpretTextAsJson={section.interpretTextAsJson ?? true}
                                            onInterpretTextAsJsonChange={(value) => setSections(prev => prev.map(s => (
                                                s.id === section.id ? { ...s, interpretTextAsJson: value } : s
                                            )))}
                                        />
                                    )}
                                    {section.visual === 'measurement-weather' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Weather"
                                            description="Shows weather conditions captured with the measurement."
                                        />
                                    )}
                                    {section.visual === 'measurement-location' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Location (GPS)"
                                            description="Shows the GPS location of the measurement on the map."
                                        />
                                    )}
                                    {section.visual === 'measurement-pattern-location' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Pattern Location"
                                            description="Shows the pattern layout with the measurement’s pattern point highlighted."
                                        />
                                    )}
                                    {section.visual === 'measurement-files' && (
                                        <SectionConfigMeasurementReport
                                            title="Measurement Files"
                                            description="Displays images and other files attached to the measurement."
                                        />
                                    )}
                                    {section.visual === 'calendar-availability' && (
                                        <SectionConfigCalendarAvailability
                                            initialConfig={{
                                                calendarShowCounts: section.calendarShowCounts,
                                                calendarShowLegend: section.calendarShowLegend,
                                                includeDescendants: section.includeDescendants,
                                            }}
                                            onChange={(c: any) => setSections(prev => prev.map(s => s.id === section.id ? { ...s, ...c } : s))}
                                        />
                                    )}
                                </Card>
                            ))}
                            <Button type="dashed" onClick={addSection} block>Add Section</Button>
                        </Space>
                    </Card>
                </Col>

                {/* Generate and Save Buttons */}
                <Col span={24}>
                    {configsChanged && generatedTemplate && (
                        <Alert message="Config changed - click regenerate" type="warning" showIcon />
                    )}
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Button type="primary" size="large" block onClick={handleGenerate} loading={generating}>
                            {generatedTemplate ? 'Regenerate Template' : 'Generate Template'}
                        </Button>
                        {generatedTemplate && (
                            <Tooltip 
                                title={configsChanged ? "Please click 'Regenerate Template' before saving" : (isEditMode ? "Update this template" : "Save this template to your company")}
                            >
                                <Button type="default" size="large" block onClick={handleSaveTemplate} disabled={configsChanged}>
                                    {isEditMode ? 'Update Template' : 'Save Template'}
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                </Col>

                {/* Preview */}
                <Col span={24}>
                    <Card title="Preview" extra={
                        <Space>
                            <Select style={{ width: 220 }} placeholder="Select area" value={previewAreaId ?? undefined} onChange={setPreviewAreaId} options={filteredAreas.map((a: any) => ({ label: a.name, value: a.id }))} showSearch />
                            {showPatternSelector && (
                                <Select 
                                    style={{ width: 200 }} 
                                    placeholder="Select pattern" 
                                    value={previewPatternId ?? undefined} 
                                    onChange={setPreviewPatternId} 
                                    options={areaPatterns.map((p: any) => ({ label: p.name, value: p.id }))} 
                                    showSearch 
                                    allowClear
                                    loading={loadingPatterns}
                                />
                            )}
                            {showPatternSelector && previewPatternId && (
                                <Select
                                    style={{ width: 220 }}
                                    placeholder="Select pattern point"
                                    value={previewPatternPointId ?? undefined}
                                    onChange={setPreviewPatternPointId}
                                    options={previewPatternPoints.map((p: any) => ({
                                        label: p.label ? `${p.label} (Point ${p.pointIndex ?? p.id})` : `Point ${p.pointIndex ?? p.id}`,
                                        value: p.id,
                                    }))}
                                    showSearch
                                    allowClear
                                    loading={loadingPatternPoints}
                                />
                            )}
                            {showMeasurementSelector && (
                                <Select
                                    style={{ width: 260 }}
                                    placeholder="Select measurement"
                                    value={previewMeasurementId ?? undefined}
                                    onChange={setPreviewMeasurementId}
                                    options={filteredMeasurements.map((m: any) => ({
                                        label: `${m.measurementCode || `Measurement ${m.id}`} · ${m.measuredAt ? measurementDateKey(m.measuredAt, m) : new Date(m.createdAt).toISOString().slice(0, 10)}`,
                                        value: m.id,
                                    }))}
                                    showSearch
                                    allowClear
                                />
                            )}
                            <DatePicker.RangePicker value={previewDateRange} onChange={r => r && r[0] && r[1] && setPreviewDateRange([r[0], r[1]])} />
                        </Space>
                    }>
                        {!previewTemplate ? (
                            <Alert message="No preview - generate template first" type="info" />
                        ) : (
                            <Report
                                template={previewTemplate}
                                areaId={previewAreaId || undefined}
                                dateFrom={previewDateFrom}
                                dateTo={previewDateTo}
                                measurementId={previewMeasurementId ?? undefined}
                                showControls={false}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Save Template Modal */}
            <Modal
                title={isEditMode ? "Update Report Template" : "Save Report Template"}
                open={showSaveModal}
                onOk={handleSaveSubmit}
                onCancel={() => {
                    setShowSaveModal(false);
                    saveForm.resetFields();
                }}
                confirmLoading={saving}
                okText={isEditMode ? "Update" : "Save"}
                cancelText="Cancel"
            >
                <Form form={saveForm} layout="vertical">
                    <Form.Item
                        label="Template Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter template name' }]}
                    >
                        <Input placeholder="e.g., Monthly Grass Report" />
                    </Form.Item>
                    <Form.Item
                        label="Description"
                        name="description"
                    >
                        <Input.TextArea rows={3} placeholder="Optional description..." />
                    </Form.Item>
                    
                    {/* Global flag toggle - Master users only */}
                    {isMaster && (
                        <Form.Item
                            label="Global Template"
                            name="isGlobal"
                            valuePropName="checked"
                            help="Global templates are visible to all companies system-wide"
                        >
                            <Switch checkedChildren="Global" unCheckedChildren="Company" />
                        </Form.Item>
                    )}
                    
                    <Form.Item
                        label="Area Types (Optional)"
                        name="areaTypeIds"
                        help="Select specific area types or leave empty for universal template"
                    >
                        <Select
                            mode="multiple"
                            options={areaTypeOpts}
                            placeholder="Select area types (or leave empty for all)"
                            showSearch
                            allowClear
                        />
                    </Form.Item>
                    
                    {/* Dynamic Alert based on global flag */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.isGlobal !== curr.isGlobal}>
                        {({ getFieldValue }) => {
                            const isGlobal = getFieldValue('isGlobal');
                            
                            if (isGlobal) {
                                return (
                                    <Alert
                                        message="Global Template"
                                        description="This template will be visible to ALL companies system-wide for the selected area types."
                                        type="warning"
                                        showIcon
                                        style={{ marginTop: 16 }}
                                    />
                                );
                            }
                            
                            return (
                                <Alert
                                    message="Template will be saved to your active company"
                                    description="This template will be accessible to all users in your currently selected company for the specified area types (or all types if none selected)."
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default ReportTemplateWizard;
